import { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { GetEventSettingsDto } from '@/types/dtos/events/response/get-event-settings.dto';
import MediaSettingsSwitchRow from '@/components/media/media-settings-switch-row';
import { mediaSettingsConfirmMessageKey } from '@/components/media/media-settings-confirm.types';
import { useTranslations } from '@/i18n/use-translations';
import { usePatchFlowMediaSettingsMutation } from '@/state/mutations/flows/use-patch-flow-media-settings';
import { useNotifications } from '@/hooks/use-notifications';
import { getObjectId } from '@/helpers/object.id.helper';
import type { FlowMediaSettingsDto } from '@/types/dtos/media/media-settings.dto';

type SessionOverrideField = 'allowSessionOverride';

type Pending = {
  field: SessionOverrideField;
  patch: Partial<FlowMediaSettingsDto>;
};

function patchToSessionOverrideField(patch: Partial<FlowMediaSettingsDto>): SessionOverrideField[] {
  return patch.speakers?.allowSessionOverride !== undefined ? ['allowSessionOverride'] : [];
}

export default function FlowMediaSettingsPanel({
  flow,
  eventSettings,
  multiFlow = false,
}: {
  flow: FlowDto;
  eventSettings?: GetEventSettingsDto | null;
  multiFlow?: boolean;
}) {
  const t = useTranslations();
  const notification = useNotifications();
  const flowOriginId = flow.origin ? getObjectId(flow.origin) : flow.id;
  const caps = flow.mediaSettingsCapabilities;
  const stored = flow.mediaSettings ?? {};

  const [pending, setPending] = useState<Pending | null>(null);
  const [optimisticChecked, setOptimisticChecked] = useState<Partial<Record<SessionOverrideField, boolean>>>({});
  const { mutateAsync } = usePatchFlowMediaSettingsMutation();

  const clearOptimistic = (fields: SessionOverrideField[]) => {
    if (fields.length === 0) return;
    setOptimisticChecked((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const field of fields) {
        if (field in next) {
          delete next[field];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  };

  const setOptimistic = (field: SessionOverrideField, value: boolean) => {
    setOptimisticChecked((prev) => ({ ...prev, [field]: value }));
  };

  const applyPatch = async (patch: Partial<FlowMediaSettingsDto>) => {
    const fields = patchToSessionOverrideField(patch);
    try {
      await mutateAsync({ flowId: flowOriginId, patch });
      notification.notifySuccess(t('media.settings.saveSuccess'));
    } catch {
      notification.notifyError(t('media.settings.saveError'));
    } finally {
      clearOptimistic(fields);
    }
  };

  const cancelPending = () => {
    if (pending) {
      clearOptimistic([pending.field]);
    }
    setPending(null);
  };

  const confirmPending = () => {
    const current = pending;
    setPending(null);
    if (current) {
      void applyPatch(current.patch);
    }
  };

  const changeSessionOverrideRef = useRef<(value: boolean) => void>(() => {});

  changeSessionOverrideRef.current = (value: boolean) => {
    setOptimistic('allowSessionOverride', value);
    const patch = { speakers: { allowSessionOverride: value } };
    if (!value) {
      setPending({ field: 'allowSessionOverride', patch });
    } else {
      void applyPatch(patch);
    }
  };

  const onSessionOverrideChange = useCallback((value: boolean) => changeSessionOverrideRef.current(value), []);

  const eventSpeakers = eventSettings?.mediaSettings?.speakers;
  const eventAllowSpeakerFlowOverride = eventSpeakers?.allowFlowOverride ?? true;

  const sessionOverrideStored = stored.speakers?.allowSessionOverride ?? true;

  const canEditFlow = caps?.canEdit ?? false;
  const canEditSessionOv = canEditFlow && eventAllowSpeakerFlowOverride;

  const sessionOverride = optimisticChecked.allowSessionOverride ?? sessionOverrideStored;

  const labels = useMemo(
    () => ({
      sessionOverride: t('media.settings.flow.allowSessionOverride'),
    }),
    [t],
  );

  const sessionOverrideConfirm =
    pending?.field === 'allowSessionOverride'
      ? {
          message: t(mediaSettingsConfirmMessageKey('flow.allowSessionOverride.off')),
          onCancel: cancelPending,
          onConfirm: confirmPending,
        }
      : null;

  return (
    <Box sx={{ py: 1 }}>
      <Stack spacing={1}>
        <MediaSettingsSwitchRow
          switchKey={`flow-allowSessionOverride-${sessionOverride}`}
          label={labels.sessionOverride}
          checked={sessionOverride}
          disabled={!canEditSessionOv}
          confirm={sessionOverrideConfirm}
          onChange={onSessionOverrideChange}
        />
        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2">{t('media.virtualSettings.flow.title')}</Typography>
        <MediaSettingsSwitchRow
          switchKey={`flow-virtual-allowSessionOverride-${stored.virtualMedia?.allowSessionOverride ?? true}`}
          label={t('media.virtualSettings.flow.allowSessionOverride')}
          checked={stored.virtualMedia?.allowSessionOverride ?? true}
          disabled={!canEditSessionOv}
          tooltip={!canEditSessionOv ? t('media.settings.tooltip.disabled.sessionOverrideFlow') : undefined}
          onChange={(v) => {
            void applyPatch({ virtualMedia: { allowSessionOverride: v } });
          }}
        />
        {multiFlow ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {t('media.virtualSettings.flow.scopeDefaultsHint')}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}
